import React, { useState, useEffect, useCallback } from 'react';
import { customersAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', customer_type:'retail', gstin:'', drug_license_no:'', contact_person:'', phone:'', email:'', address_line1:'', city:'', state:'', state_code:'', pincode:'', payment_terms:0, credit_limit:0 });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await customersAPI.list({ search, limit: 200 }); setCustomers(r.data.customers); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name) return toast.error('Name is required');
    try {
      await customersAPI.create(form);
      toast.success('Customer created');
      setShowForm(false);
      setForm({ name:'', customer_type:'retail', gstin:'', drug_license_no:'', contact_person:'', phone:'', email:'', address_line1:'', city:'', state:'', state_code:'', pincode:'', payment_terms:0, credit_limit:0 });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const set = (f) => (e) => setForm(p => ({...p, [f]: e.target.value}));
  const fmt = (n) => Number(n||0).toLocaleString('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0});
  const typeColors = { retail:'badge-blue', wholesale:'badge-green', hospital:'badge-purple', distributor:'badge-orange', institution:'badge-gray' };

  return (
    <div>
      <div className="page-header">
        <div><h1>Customers</h1><p className="subtitle">{customers.length} customers</p></div>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">+ Add Customer</button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowForm(false)}>
          <div className="modal modal-lg">
            <div className="modal-header"><h2>New Customer</h2><button onClick={() => setShowForm(false)} className="btn btn-icon">✕</button></div>
            <div className="modal-body">
              <div className="form-row form-row-3">
                <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
                <div className="form-group"><label className="form-label">Type</label>
                  <select className="form-select" value={form.customer_type} onChange={set('customer_type')}>
                    <option value="retail">Retail</option><option value="wholesale">Wholesale</option>
                    <option value="hospital">Hospital</option><option value="distributor">Distributor</option>
                    <option value="institution">Institution</option>
                  </select></div>
                <div className="form-group"><label className="form-label">GSTIN</label><input className="form-input" value={form.gstin} onChange={set('gstin')} /></div>
              </div>
              <div className="form-row form-row-3">
                <div className="form-group"><label className="form-label">Drug License No</label><input className="form-input" value={form.drug_license_no} onChange={set('drug_license_no')} /></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={set('phone')} /></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.email} onChange={set('email')} /></div>
              </div>
              <div className="form-row form-row-4">
                <div className="form-group"><label className="form-label">Address</label><input className="form-input" value={form.address_line1} onChange={set('address_line1')} /></div>
                <div className="form-group"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={set('city')} /></div>
                <div className="form-group"><label className="form-label">State</label><input className="form-input" value={form.state} onChange={set('state')} /></div>
                <div className="form-group"><label className="form-label">State Code</label><input className="form-input" value={form.state_code} onChange={set('state_code')} maxLength={2} /></div>
              </div>
              <div className="form-row form-row-3">
                <div className="form-group"><label className="form-label">Pincode</label><input className="form-input" value={form.pincode} onChange={set('pincode')} /></div>
                <div className="form-group"><label className="form-label">Payment Terms (days)</label><input type="number" className="form-input" value={form.payment_terms} onChange={set('payment_terms')} /></div>
                <div className="form-group"><label className="form-label">Credit Limit (₹)</label><input type="number" className="form-input" value={form.credit_limit} onChange={set('credit_limit')} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowForm(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleCreate} className="btn btn-primary">Create Customer</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body" style={{paddingBottom:0}}>
          <div className="search-bar">
            <div className="search-input-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner" /></div> : (
            <table>
              <thead><tr><th>Name</th><th>Type</th><th>GSTIN</th><th>Drug License</th><th>Phone</th><th>City</th><th style={{textAlign:'right'}}>Balance</th><th>Status</th></tr></thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id}>
                    <td style={{fontWeight:600}}>{c.name}</td>
                    <td><span className={`badge ${typeColors[c.customer_type] || 'badge-gray'}`}>{c.customer_type}</span></td>
                    <td className="td-mono">{c.gstin || '-'}</td>
                    <td className="td-mono">{c.drug_license_no || '-'}</td>
                    <td className="text-secondary">{c.phone || '-'}</td>
                    <td className="text-secondary">{c.city || '-'}</td>
                    <td className="td-amount" style={{color: Number(c.current_balance)>0 ? 'var(--danger)' : 'inherit'}}>{fmt(c.current_balance)}</td>
                    <td><span className={`badge ${c.status==='active' ? 'badge-green' : 'badge-red'}`}>{c.status}</span></td>
                  </tr>
                ))}
                {customers.length===0 && <tr><td colSpan={8} className="text-center text-secondary" style={{padding:40}}>No customers found</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
